import { FormattedMessage, useIntl } from 'react-intl';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons/faInfoCircle';

import { Gap } from 'ui-components/components/flex';
import { FlexColumn } from 'ui-components/components/flex/column';
import { FlexRow } from 'ui-components/components/flex/row';
import { InlineMessage, INLINE_MESSAGE_TYPES } from 'ui-components/components/inline-message';
import { NumberInput } from 'ui-components/components/number-input';
import { Tooltip } from 'ui-components/components/tooltip';

import { InputLabel } from '../../../../../../../../../address/input-label';
import { SimpleDropdown } from '../../../../../../../../../components/simple-dropdown';
import type { ProductUnitOfMeasureResponse } from '../../../../uom-types';
import { QUANTITY_MAX } from '../../uom-modal.constants';
import { messages } from '../../uom-modal.messages';
import style from '../../uom-modal.module.css';

type UnitRowItem = { key: string; name: string; disabled?: boolean };

type UnitRowProps = {
  containsItems: UnitRowItem[];
  containsError: string | null;
  conversionSummary: string;
  currentContainsUnit: ProductUnitOfMeasureResponse | null;
  currentTypeName: string;
  quantityError: string | null;
  quantityValue: string;
  typeError: string | null;
  typeItems: { key: string; name: string }[];
  typeValue: string;
  containsValue: string;
  onContainsChange: (key: string) => void;
  onTypeChange: (key: string) => void;
  setQuantityError: (error: string | null) => void;
  updateQuantity: (value: string) => void;
};

export const UnitRow = ({
  containsItems,
  containsError,
  conversionSummary,
  currentContainsUnit,
  currentTypeName,
  quantityError,
  quantityValue,
  typeError,
  typeItems,
  typeValue,
  containsValue,
  onContainsChange,
  onTypeChange,
  setQuantityError,
  updateQuantity,
}: UnitRowProps) => {
  const intl = useIntl();

  const handleQuantityChange = (v: number | undefined) => {
    const rounded = v != null ? Math.round(v) : null;
    updateQuantity(rounded != null ? String(rounded) : '');
    let qtyError: string | null = null;
    if (rounded == null || rounded < 1) qtyError = 'required';
    else if (rounded > QUANTITY_MAX) qtyError = 'tooLarge';
    setQuantityError(qtyError);
  };

  return (
    <>
      <FlexRow className={style['flex-row']} gap={Gap.Space16}>
        <FlexColumn className={style['flex-column']}>
          <InputLabel hasError={!!typeError} required>
            <FormattedMessage {...messages.typeLabel} />
            <Tooltip text={intl.formatMessage(messages.typeHelper)}>
              <span className={style['label-info-icon']}>
                <FontAwesomeIcon icon={faInfoCircle} />
              </span>
            </Tooltip>
          </InputLabel>
          <SimpleDropdown fullWidth hasError={!!typeError} items={typeItems} onItemSelected={onTypeChange} selectedItemKey={typeValue} />
          {typeError && (
            <InlineMessage type={INLINE_MESSAGE_TYPES.error}>
              <FormattedMessage {...messages.typeRequired} />
            </InlineMessage>
          )}
        </FlexColumn>

        <FlexColumn className={style['flex-column']}>
          <InputLabel hasError={!!quantityError} required>
            <FormattedMessage {...messages.quantityLabel} />
            {currentTypeName && (
              <Tooltip
                text={intl.formatMessage(messages.quantityHelper, {
                  typeName: currentTypeName,
                  containsName: currentContainsUnit?.uomTypeName ?? '',
                })}
              >
                <span className={style['label-info-icon']}>
                  <FontAwesomeIcon icon={faInfoCircle} />
                </span>
              </Tooltip>
            )}
          </InputLabel>
          <NumberInput
            className={style['quantity-input']}
            label={intl.formatMessage(messages.quantityLabel)}
            min={1}
            onChange={handleQuantityChange}
            value={quantityValue !== '' ? Number(quantityValue) : null}
          />
          {quantityError === 'required' && (
            <InlineMessage type={INLINE_MESSAGE_TYPES.error}>
              <FormattedMessage {...messages.quantityRequired} />
            </InlineMessage>
          )}
          {quantityError === 'tooLarge' && (
            <InlineMessage type={INLINE_MESSAGE_TYPES.error}>
              <FormattedMessage {...messages.quantityTooLarge} />
            </InlineMessage>
          )}
        </FlexColumn>

        <FlexColumn className={style['flex-column']}>
          <InputLabel hasError={!!containsError} required>
            <FormattedMessage {...messages.containsLabel} />
            {currentTypeName && (
              <Tooltip text={intl.formatMessage(messages.containsHelper, { typeName: currentTypeName })}>
                <span className={style['label-info-icon']}>
                  <FontAwesomeIcon icon={faInfoCircle} />
                </span>
              </Tooltip>
            )}
          </InputLabel>
          <SimpleDropdown
            fullWidth
            hasError={!!containsError}
            items={containsItems}
            onItemSelected={onContainsChange}
            selectedItemKey={containsValue}
          />
          {containsError && (
            <InlineMessage type={INLINE_MESSAGE_TYPES.error}>
              <FormattedMessage {...messages.containsRequired} />
            </InlineMessage>
          )}
        </FlexColumn>
      </FlexRow>

      <div className={style['conversion-preview']}>{conversionSummary}</div>
    </>
  );
};
